import { v4 as uuidv4 } from 'uuid'

import { CustomEvents } from '@/constants'
import {
  ActionType,
  ContentType,
  LessonCompletionMigration,
  SyncEvent,
  SyncFetchEventsParams,
  SyncValidateParams,
  UserInfo
} from '@/lib/sync'
import SyncProxy from '@/proxies/SyncProxy'

import { STAR_EXPERIENCE_RECEIVED, SYNC_USER_INFO_ERROR } from './constants'
import { SyncLocalStorage } from './storage'

type SyncServiceOptions = {
  syncIntervalSec?: number
  appId: string
  getUserInfo: () => UserInfo | null
  applyEvents: (events: SyncEvent[]) => void
  onLastSyncChange?: (timestamp: number) => void
  storage?: SyncLocalStorage
  proxy?: SyncProxy
}

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms))

export class SyncService {
  private syncIntervalSec: number
  private lastSync = 0
  private periodicTask: number | null = null
  private startTimeout: number | null = null
  private isListeningFromServer = false
  private readonly appId: string
  private readonly getUdid: () => string
  private readonly getUserInfo: () => UserInfo | null
  private readonly applyEvents: (events: SyncEvent[]) => void
  private readonly onLastSyncChange: (timestamp: number) => void
  private readonly storage: SyncLocalStorage
  private readonly proxy: SyncProxy
  private serverListener: ((event: Event) => void) | null = null

  constructor(options: SyncServiceOptions) {
    this.syncIntervalSec = options.syncIntervalSec ?? 60
    this.appId = options.appId
    this.getUdid = options.getUdid
    this.getUserInfo = options.getUserInfo
    this.applyEvents = options.applyEvents
    this.onLastSyncChange = options.onLastSyncChange ?? (() => {})
    this.storage = options.storage ?? new SyncLocalStorage()
    this.proxy = options.proxy ?? new SyncProxy()
  }

  setSyncInterval(seconds: number) {
    this.syncIntervalSec = seconds
  }

  getLastSync() {
    return this.lastSync
  }

  async initCompletion() {
    await this.initFromDb()

    this.startListeningFromServer()
  }

  async initFromDb() {
    const all = await this.storage.getAllEvents()
    if (all.length) {
      this.applyEvents(all)
    }
  }

  startListeningFromServer() {
    if (this.isListeningFromServer) {
      return
    }

    this.serverListener = (event: Event) => {
      const detail = (event as CustomEvent<SyncEvent[]>).detail
      if (Array.isArray(detail)) {
        this.applyEvents(detail)
      }
    }

    window.addEventListener(CustomEvents.SYNC_SAVED, this.serverListener)
    this.isListeningFromServer = true
  }

  stopListeningFromServer() {
    if (this.serverListener) {
      window.removeEventListener(CustomEvents.SYNC_SAVED, this.serverListener)
    }
    this.isListeningFromServer = false
    this.serverListener = null
  }

  startSyncing() {
    this.stopSyncing()
    this.periodicSync()
  }

  stopSyncing() {
    if (this.startTimeout) {
      clearTimeout(this.startTimeout)
      this.startTimeout = null
    }

    if (this.periodicTask) {
      clearTimeout(this.periodicTask)
      this.periodicTask = null
    }
  }

  async periodicSync() {
    const now = Date.now()
    const elapsedSec = (now - this.lastSync) / 1000

    if (elapsedSec >= this.syncIntervalSec) {
      try {
        await this.syncEvents()
        this.updateLastSync(now)
      } catch (error) {
        console.error('Sync failed', error)
      }
    }

    this.periodicTask = window.setTimeout(
      () => this.periodicSync(),
      this.syncIntervalSec * 1000
    )
  }

  async forceSync(delayed = true) {
    if (delayed) {
      await sleep(5000)
    }

    await this.syncEvents()
    this.updateLastSync(Date.now())
  }

  async syncEvents() {
    await this.uploadWithRetry()
    await this.download()
  }

  async createEvent(
    contentType: ContentType,
    actionType: ActionType,
    modifier: (event: SyncEvent) => void
  ) {
    const userInfo = this.getUserInfo()

    if (!userInfo) {
      throw new Error(SYNC_USER_INFO_ERROR)
    }

    const timezone = this.resolveTimezone()

    const event: SyncEvent = {
      transaction_id: uuidv4(),
      content_type: contentType,
      action_type: actionType,
      content_id: '',
      learning_language: userInfo.learningLanguage,
      native_language: userInfo.nativeLanguage,
      udid: userInfo.udid,
      app_id: this.appId,
      created_at: Date.now(),
      timezone
    }

    modifier(event)

    if (contentType === ContentType.WORD && !event.content_id) {
      throw new Error('SyncService: word event requires content_id')
    }

    await this.storage.saveEvents([event], false)
    this.applyEvents([event])
    await this.forceSync(true)
  }

  async saveLessonCompletionEvents(lessons: LessonCompletionMigration[]) {
    if (!lessons?.length) {
      return
    }

    const userInfo = this.getUserInfo()

    if (!userInfo) {
      throw new Error(SYNC_USER_INFO_ERROR)
    }

    const now = Date.now()
    const timezone = this.resolveTimezone()

    const events = lessons
      .filter((lesson) => lesson.id && lesson.starsEarned > 0)
      .map((lesson) => {
        const event: SyncEvent = {
          transaction_id: uuidv4(),
          content_type: ContentType.LESSON,
          action_type: ActionType.LESSON_COMPLETED,
          content_id: lesson.id,
          learning_language: userInfo.learningLanguage,
          native_language: userInfo.nativeLanguage,
          udid: userInfo.udid,
          app_id: this.appId,
          created_at: now,
          timezone,
          value_integer: lesson.starsEarned,
          experience_rcvd: lesson.starsEarned * STAR_EXPERIENCE_RECEIVED
        }

        return event
      })

    if (!events.length) {
      return
    }

    await this.storage.saveEvents(events, false)
    this.applyEvents(events)
    await this.forceSync(false)
  }

  setLastSync(timestamp: number) {
    this.lastSync = timestamp
    this.onLastSyncChange(timestamp)
  }

  shutdown() {
    this.stopSyncing()
    this.stopListeningFromServer()
  }

  getStorageEvents() {
    return this.storage.getAllEvents()
  }

  private updateLastSync(timestamp: number) {
    this.lastSync = timestamp
    this.onLastSyncChange(timestamp)
  }

  private resolveTimezone() {
    const now = new Date()
    const tzOffset = -now.getTimezoneOffset()
    const hours = Math.floor(Math.abs(tzOffset) / 60)
    const minutes = Math.abs(tzOffset) % 60
    const sign = tzOffset >= 0 ? '+' : '-'
    return `${sign}${String(hours).padStart(2, '0')}${String(minutes).padStart(2, '0')}`
  }

  private async uploadWithRetry() {
    const maxAttempts = 10
    let attempts = 0

    while (attempts < maxAttempts) {
      try {
        const unsynced = await this.storage.getUnsyncedEvents()

        if (unsynced.length === 0) {
          return
        }

        await Promise.all(unsynced.map((event) => this.uploadEvents(event)))

        await this.storage.markEventsAsSynced(
          unsynced.map((event) => event.transaction_id)
        )

        return
      } catch (error) {
        const message = (error as Error)?.message || JSON.stringify(error)
        const match = message?.match?.(/transaction_id=([a-zA-Z0-9-]+)/)

        if (match?.length > 1) {
          await this.storage.deleteEvent(match[1])
        }

        attempts += 1
        const delay = Math.min(10000, 1000 * attempts)
        await sleep(delay)
      }
    }

    if (attempts === maxAttempts) {
      throw new Error('Upload failed after max retries')
    }
  }

  private async download() {
    const last = this.storage.getLastSyncTimestamp()
    const events = await this.fetchEvents(last)
    const valid = events.filter(
      (event: SyncEvent) => event.native_language && event.learning_language
    )

    if (valid.length > 0) {
      await this.storage.saveEvents(valid, true)

      const latestCreatedAt = valid.reduce((max, event) => {
        const timestamp = Number(event.created_at)

        if (!Number.isFinite(timestamp)) {
          return max
        }

        return Math.max(max, timestamp)
      }, 0)

      const nextTimestamp = Math.max(latestCreatedAt, last)
      this.storage.setLastSyncTimestamp(nextTimestamp)

      this.applyEvents(valid)
    }
  }

  private async fetchEvents(since: number) {
    const params: SyncFetchEventsParams = {}

    if (since >= 0) {
      params.since = since
    }

    const events = await this.proxy.sync(params)
    return events || []
  }

  private uploadEvents(event: SyncValidateParams) {
    return this.proxy.validate(event)
  }
}
